import {Vector3} from 'three';

import {
  checkExhaustive,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {QueryOne} from 'external/dev_april_corgi+/js/corgi/dom';
import {
  CorgiEvent,
  DOM_FOCUS,
  DOM_KEYBOARD,
  DOM_MOUSE,
  DOM_POINTER,
} from 'external/dev_april_corgi+/js/corgi/events';
import {CHANGED, PRESSED} from 'external/dev_april_corgi+/js/emu/events';

import {Graph} from './execution/graph';

import {GraphController} from './graph_controller';

export interface Divider {
  kind: 'divider';
}

export interface Menu {
  kind: 'menu';
  label: string;
  items: Array<Divider | Menu | MenuItem>;
}

export interface MenuItem {
  kind: 'menu_item';
  label: string;
  action: (args: Args) => void;
}

export interface Args {
  controller: GraphController;
  graph: Graph;
  position: Vector3;
}

export interface State {
  active: number[];
  items: Array<Divider | Menu | MenuItem>;
  mode: 'normal' | 'searching';
  search: string;
  searchIndex: number;
  searchResults: Array<{context: string[]; item: MenuItem}>;
}

export class MenuController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly context: Args;

  constructor(response: Response<MenuController>) {
    super(response);
    this.context = response.args;

    if (this.state.mode === 'searching') {
      this.searchNeedle('');
    }
  }

  close(): void {
    this.root.remove();
  }

  maybeClickClose(e: CorgiEvent<typeof DOM_MOUSE>): void {
    if (e.targetElement.element() === this.root) {
      this.close();
    }
  }

  focused(e: CorgiEvent<typeof DOM_FOCUS>): void {
    this.updateState({
      ...this.state,
      active: this.calculateActive(e.actionElement),
    });
  }

  keyPressed(e: CorgiEvent<typeof DOM_KEYBOARD>): void {
    const active = this.state.active;
    let newActive = undefined;
    if (e.detail.key === 'Enter') {
      this.select(active);
    } else if (e.detail.key === 'Escape') {
      this.close();
    } else if (e.detail.key === 'ArrowLeft') {
      if (active.length === 1) {
        this.close();
      } else {
        newActive = active.slice(0, active.length - 1);
      }
    } else if (e.detail.key === 'ArrowRight') {
      const {item} = this.findActiveItem(active);
      if (item.kind === 'menu') {
        newActive = [...active, 0];
      }
    } else if (e.detail.key === 'ArrowDown') {
      const {parent} = this.findActiveItem(active);
      newActive = active.slice(0, active.length - 1);
      const current = active[active.length - 1];
      const count = parent.items.length;
      newActive.push((current + 1) % count);
    } else if (e.detail.key === 'ArrowUp') {
      const {parent} = this.findActiveItem(active);
      newActive = active.slice(0, active.length - 1);
      const current = active[active.length - 1];
      const count = parent.items.length;
      newActive.push((current - 1 + count) % count);
    } else if (e.detail.key === 'Escape') {
      this.close();
    } else if (e.detail.key.length === 1) {
      // .length === 1 is a simple check to avoid Meta, Shift, F12, etc
      return this.searchChanged({
        actionElement: e.actionElement,
        targetElement: e.targetElement,
        detail: {value: this.state.search + e.detail.key},
      });
    }

    if (newActive) {
      this.updateState({
        ...this.state,
        active: newActive,
      });
    }
  }

  pointerEntered(e: CorgiEvent<typeof DOM_POINTER>): void {
    this.updateState({
      ...this.state,
      active: this.calculateActive(e.actionElement),
    });
  }

  selected(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.select(this.calculateActive(e.actionElement));
  }

  searchAction(): void {
    const {item} = this.state.searchResults[this.state.searchIndex];
    item.action(this.context);
    this.close();
  }

  searchChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.searchNeedle(e.detail.value);
  }

  private searchNeedle(query: string): void {
    const results: Array<{
      context: string[];
      item: MenuItem;
      score: number;
    }> = [];
    const needle = query.toLowerCase();
    const dive = (item: Divider | Menu | MenuItem, context: string[]) => {
      if (item.kind === 'menu') {
        for (const subitem of item.items) {
          dive(subitem, [...context, item.label]);
        }
      } else if (item.kind === 'menu_item') {
        const label = item.label.toLocaleLowerCase();
        const fq = [context, item.label].join(' ').toLocaleLowerCase();
        const scored = [];
        for (const haystack of [label, fq]) {
          let score = undefined;
          if (haystack.startsWith(needle)) {
            // Does it start with the needle?
            score = -needle.length; // the longer the match the better it is
          } else if (haystack.indexOf(needle) >= 0) {
            // Is the needle present anywhere?
            score = 100 - needle.length;
          } else {
            // Can we find the needle anywhere in the string?
            let first = -1;
            let last = -1;
            for (const c of needle) {
              for (; last < haystack.length; ++last) {
                if (c === haystack[last]) {
                  if (first < 0) {
                    first = last;
                  }
                  break;
                }
              }
            }
            if (last < haystack.length) {
              score = 200 - needle.length + (last - first); // how big of a stretch is it?
            }
          }

          if (score !== undefined) {
            scored.push({
              context,
              item,
              score,
            });
          }
        }
        scored.sort((a, b) => a.score - b.score);
        const maybeFirst = scored[0];
        if (maybeFirst) {
          results.push(maybeFirst);
        }
      } else if (item.kind === 'divider') {
        // who cares
      } else {
        throw checkExhaustive(item);
      }
    };
    for (const item of this.state.items) {
      dive(item, []);
    }

    results.sort((a, b) => a.score - b.score);

    this.updateState({
      ...this.state,
      mode: 'searching',
      search: query,
      searchIndex: 0,
      searchResults: results,
    });
  }

  searchPointerEntered(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.updateState({
      ...this.state,
      searchIndex: checkExists(e.actionElement.data('i')).number(),
    });
  }

  searchPressed(e: CorgiEvent<typeof PRESSED>): void {
    let direction;
    if (e.detail.key === 'ArrowDown') {
      direction = 1;
    } else if (e.detail.key === 'ArrowUp') {
      direction = -1;
    } else if (e.detail.key === 'Escape') {
      return this.close();
    } else {
      return;
    }
    const current = this.state.searchIndex;
    const length = this.state.searchResults.length;
    this.updateState({
      ...this.state,
      searchIndex: (current + direction + length) % length,
    });
  }

  private select(active: number[]): void {
    const {item} = this.findActiveItem(active);
    if (item.kind === 'menu_item') {
      item.action(this.context);
      this.close();
    }
  }

  private calculateActive(target: QueryOne): number[] {
    target.element().focus();

    const chain = [];
    let cursor = target;
    while (this.root.contains(cursor.element())) {
      chain.unshift(checkExists(cursor.data('index')).number());
      try {
        cursor = cursor.parent('[data-index]');
      } catch (e: unknown) {
        break;
      }
    }
    return chain;
  }

  private findActiveItem(active: number[]): {
    parent: Menu;
    item: Divider | Menu | MenuItem;
  } {
    let cursor: Menu = {
      kind: 'menu',
      label: 'garbage',
      items: this.state.items,
    };
    for (let i = 0; i < active.length - 1; ++i) {
      cursor = cursor.items[active[i]] as Menu;
    }
    return {parent: cursor, item: cursor.items[active[active.length - 1]]};
  }
}
